import { getDefaultLayout } from './dashboard.defaults';
import { WIDGET_REGISTRY } from './dashboard.registry';
import type {
  ColSpan,
  DashboardLayout,
  DashboardType,
  PlatformLayout,
  WidgetId,
  WidgetLayoutItem,
} from './dashboard.types';

export type DashboardPlatform = keyof DashboardLayout;

/** What the API hands back: an opaque JSON blob, so nothing about it is guaranteed. */
type StoredWidget = { id?: unknown; visible?: unknown; order?: unknown; col_span?: unknown };

const isWidgetId = (id: unknown): id is WidgetId => typeof id === 'string' && id in WIDGET_REGISTRY;

const readWidgets = (value: unknown): StoredWidget[] =>
  Array.isArray(value)
    ? value.filter((item): item is StoredWidget => !!item && typeof item === 'object')
    : [];

const readOrder = (value: unknown): number => (typeof value === 'number' ? value : 0);

/**
 * A locked section is part of the platform's frame rather than a choice — the
 * dashboard paints it whatever the stored flag says, so the layout agrees.
 */
export const isLockedWidget = (id: WidgetId, platform: DashboardPlatform): boolean => {
  const def = WIDGET_REGISTRY[id];
  return Boolean(platform === 'mobile' ? def.lockedOnMobile : def.lockedOnDesktop);
};

/** Mobile stacks one section per row; only the desktop grid has spans to keep. */
const colSpan = (stored: unknown, id: WidgetId, platform: DashboardPlatform): ColSpan => {
  const def = WIDGET_REGISTRY[id];
  if (platform === 'mobile') return 1;
  const span = typeof stored === 'number' ? stored : def.defaultColSpan;
  return Math.min(def.maxColSpan, Math.max(def.minColSpan, span)) as ColSpan;
};

/**
 * Reconciles whatever is stored for one platform with the role's defaults:
 * unknown ids and duplicates go, sections the account has since gained are
 * appended hidden-free at the end, and `order` is re-derived from position so
 * it is always a dense 0..n-1 run.
 */
const normalizePlatform = (
  stored: unknown,
  fallback: readonly WidgetLayoutItem[],
  platform: DashboardPlatform,
): PlatformLayout => {
  const seen = new Set<WidgetId>();
  const widgets: WidgetLayoutItem[] = [];

  const take = (item: StoredWidget) => {
    const { id } = item;
    if (!isWidgetId(id) || seen.has(id)) return;
    seen.add(id);
    widgets.push({
      id,
      visible: isLockedWidget(id, platform) ? true : item.visible !== false,
      order: 0,
      col_span: colSpan(item.col_span, id, platform),
    });
  };

  [...readWidgets(stored)].sort((a, b) => readOrder(a.order) - readOrder(b.order)).forEach(take);
  fallback.forEach(take);

  // Locked sections lead the stack, matching how the dashboard paints them.
  const pinned = widgets.filter((widget) => isLockedWidget(widget.id, platform));
  const rest = widgets.filter((widget) => !isLockedWidget(widget.id, platform));

  return { widgets: [...pinned, ...rest].map((widget, index) => ({ ...widget, order: index })) };
};

/**
 * The single reading of a stored layout. Everything that renders a dashboard
 * goes through here, so a layout saved before a widget was added — or by an
 * account with different permissions — still resolves instead of being thrown
 * away and overwritten with defaults.
 */
export const normalizeDashboardLayout = (stored: unknown, type: DashboardType): DashboardLayout => {
  const defaults = getDefaultLayout(type);
  const layout = (stored ?? {}) as {
    desktop?: { widgets?: unknown };
    mobile?: { widgets?: unknown };
  };

  return {
    desktop: normalizePlatform(layout.desktop?.widgets, defaults.desktop.widgets, 'desktop'),
    mobile: normalizePlatform(layout.mobile?.widgets, defaults.mobile.widgets, 'mobile'),
  };
};
